import {
  useState,
  useRef,
  useCallback,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';
import { useReactToPrint } from 'react-to-print';

/** Browser Detection */
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Copy styles from one document to another.
 * @param sourceDoc - source document
 * @param targetDoc - target document
 */
const copyStyles = (sourceDoc: Document, targetDoc: Document) => {
  const styleSheets = Array.from(sourceDoc.styleSheets).filter(
    (styleSheet) =>
      !styleSheet.href || styleSheet.href.startsWith(window.location.origin)
  );

  styleSheets.forEach((styleSheet) => {
    if (styleSheet instanceof CSSStyleSheet && styleSheet.cssRules) {
      // true for inline styles
      const newStyleEl = sourceDoc.createElement('style');

      Array.from(styleSheet.cssRules).forEach((cssRule) => {
        newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
      });

      targetDoc.head.appendChild(newStyleEl);
    } else if (styleSheet.href) {
      // true for stylesheets loaded from a URL
      const newLinkEl = sourceDoc.createElement('link');

      newLinkEl.rel = 'stylesheet';
      newLinkEl.href = styleSheet.href;
      targetDoc.head.appendChild(newLinkEl);
    }
  });
  Array.from(sourceDoc.fonts).forEach((font) => targetDoc.fonts.add(font));
};

/**
 * Print Handler for Safari
 * @param componentRef - reference to component which has the print layout
 * This is done to fix the issue in Safari Browsers where the print dialog is not shown after first print
 */
const getSafariPrintHandler = (
  componentRef: MutableRefObject<HTMLDivElement | null>
) => {
  /** Open a new Pop-Up Window */
  const popupWindow = window.open(
    '',
    '',
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=yes, copyhistory=no, fullscreen=yes`
  );

  /** Add print layout component to the DOM of new pop-up window */
  const printDoc = popupWindow?.document;
  printDoc?.write(`
  <!DOCTYPE>
    <html>
      <head>
        <title></title>
      </head>
      <body>
        <div>
          ${componentRef.current?.outerHTML || ''}
        </div>
      </body>
    </html>
  `);

  /** Add Styles to Pop-Up Window  (remove date-time text while print) */
  const styleEl = printDoc?.createElement('style');
  if (styleEl && printDoc) {
    styleEl?.appendChild(
      printDoc?.createTextNode(
        '@page { size: auto;  margin: 0mm; } @media print { body { -webkit-print-color-adjust: exact; } }'
      )
    );
    printDoc?.head.appendChild(styleEl);
  }

  /** Copy Styles from Print Component to Pop-Up Window */
  if (componentRef.current?.ownerDocument && printDoc) {
    copyStyles(componentRef.current?.ownerDocument, printDoc);
  }

  /** Clean Up */
  printDoc?.close();
  popupWindow?.print();
  popupWindow?.close();
};

/**
 * usePrint Hook
 * @param usePrintState - (optional) if true, user have to setPrintState to true to print before calling handlePrint
 * and handlePrint will only execute once irrespective of the number of times handlePrint is called.
 *
 * To call execute handlePrint again, user have to setPrintState to true
 * @returns object containing componentRef, handlePrint, setPrintState
 */
const usePrint = (
  usePrintState?: boolean
): {
  /** Reference to component which has the print layout */
  componentRef: MutableRefObject<null>;
  /** Print Handle Function Opens Browser Print Dialog */
  handlePrint: () => void;
  /** Print State setState */
  setPrintState: Dispatch<SetStateAction<boolean>>;
} => {
  /** Reference to component which has the print layout */
  const componentRef = useRef(null);

  /** Print State */
  const [printState, setPrintState] = useState(false);

  /** useReactToPrint Hook */
  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  /** Print Handle Function Opens Browser Print Dialog */
  const handlePrint = useCallback(() => {
    if (!usePrintState) {
      if (isSafari) {
        return getSafariPrintHandler(componentRef);
      }

      return print();
    }

    if (printState) {
      setPrintState(false);
      if (isSafari) {
        return getSafariPrintHandler(componentRef);
      }

      return print();
    }

    return () => {};
  }, [print, printState, usePrintState]);

  return {
    componentRef,
    handlePrint,
    setPrintState,
  };
};

export default usePrint;
